import React from 'react'
import { addPricingToBomItems, generateFullBom, InflatedProductBomWithRoom, PricingParams } from './bom/bomUtils'
import { Room } from './list/ListTypes'
import DisclaimerBox from './list/DisclaimerBox'
import lodash from 'lodash'
import { RoomHeader, RoomSection } from './list/RoomSection'

import './BomScreen.scss'
import ExpandToggle from './components/ExpandToggle'
import { SectionHeaderRow } from './list/SectionHeaderRow'
import { useProductPricesWithContext } from './hooks/useProductPrices'
import {
  ProductPricingContext,
  ProductPriceNoData,
  ProductPriceUnavailable,
} from './productPricing/ProductPricingContext'
import { ComponentListItemRow } from './components/ComponentListItemRow'
import FinishedProductComponentBreakdown from './FinishedProductComponentBreakdown'
import { isProductApiAvailable } from './productDataApi/productDataApi'


export const RawBomScreen: React.FC<{
  products: readonly InflatedProductBomWithRoom[]
  rooms: readonly Room[]
  onClickDownloadList: () => void
}> = ({ products, rooms, onClickDownloadList }) => {
  const productsByRoom = lodash.groupBy(products, it => it.roomId ?? '')

  const roomsToShow = [{
    name: '',
    uid: '',
  }].concat(rooms)

  const [listingShown, setListingShown] = React.useState(false)
  const [bomShown, setBomShown] = React.useState(false)

  const pricingContext = React.useContext(ProductPricingContext)
  const { getPackQuantity } = pricingContext

  const fullBom = generateFullBom(products, getPackQuantity)
  const { priceMap, isFetching: isFetchingPricing, priceTypeLabel } = useProductPricesWithContext(fullBom.map(it => it.itemNo), pricingContext)
  const pricingParams: PricingParams | null = isProductApiAvailable() ? { priceMap, label: priceTypeLabel } : null
  const fullBomWithPricing = addPricingToBomItems(fullBom, pricingParams)

  return (
    <div className="BomScreen" style={{ flex: '1 1 0' }}>
      <DisclaimerBox />
      <div style={{ padding: '0 0 10px 0' }}>
        <div>
          <SectionHeaderRow title="Bill of Materials"/>
          <SectionHeaderRow divider>
            <h3>Product Listing</h3>
            <ExpandToggle
              expanded={listingShown}
              onClick={() => setListingShown(it => !it)}
            />
          </SectionHeaderRow>
        </div>

        {
          listingShown && roomsToShow.map(room => {
            const productsInRoom = productsByRoom[room.uid] ?? []
            const isUnroomedSection = room.uid === ''
            if (isUnroomedSection && productsInRoom.length === 0) {
              return null
            }

            return (
              <RoomSection key={room.uid}>
                {
                  <RoomHeader>
                    {isUnroomedSection ? 'Not in a Room' : room.name}
                  </RoomHeader>
                }
                {
                  productsInRoom.map((bom, index) => (
                    <FinishedProductComponentBreakdown bom={bom} key={index}/>
                  ))
                }
              </RoomSection>
            )
          })
        }
        <div>
          <SectionHeaderRow
            divider
            title="Component Listing"
          >
            <ExpandToggle
              expanded={bomShown}
              onClick={() => setBomShown(it => !it)}
            />
          </SectionHeaderRow>
          {
            bomShown && (
              <div className="bomGrid">
                {
                  fullBomWithPricing.map(item => {
                    return (
                      <ComponentListItemRow
                        key={item.itemNo}
                        bomItem={item}
                        displayName={item.itemNo}
                        description={item.description}
                        price={item.price ?? ProductPriceUnavailable}
                        priceLoading={item.price === ProductPriceNoData && isFetchingPricing}
                        priceTypeLabel={priceTypeLabel}
                        showPrice={pricingContext.isEnabled}
                      />
                    )
                  })
                }
              </div>
            )
          }
        </div>
      </div>
    </div>
  )
}